import {
  FormControl,
  OutlinedInput,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { ChangeEventHandler, FC, useEffect, useMemo, useState } from "react";
import useStyles from "./styles";
import { CustomTypography } from "../CustomTypography/CustomTypography";
import { CustomButtonTable } from "../CustomButtonTable/CustomButtonTable";
import { CustomButtonModal } from "../CustomButtonModal/CustomButtonModal";
import { Icon } from "../Icon/Icon";
import IconButton from "@mui/material/IconButton";
import { colors } from "../../styles/colors";
import { CustomCheckBox } from "../CustomCheckBox/CustomCheckBox";
import { CustomIconButton } from "../CustomIconButton/CustomIconButton";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ROWS_PER_PAGE_OPTIONS,
  DEFAULT_ROWS_PER_PAGE,
} from "../../hooks/useServerPagination";

/**
 * Numbered pager rendered inside MUI's TablePagination actions slot.
 * Extracted as a stable component (instead of an inline arrow) so it is not
 * remounted on every parent render, which would otherwise drop focus and
 * re-trigger enter animations on each page change.
 */
const PaginationActions: FC<{
  count: number;
  page: number; // 0-based
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
}> = ({ count, page, rowsPerPage, onPageChange }) => {
  const pageCount = Math.max(1, Math.ceil((count || 0) / rowsPerPage));
  if (pageCount <= 1) return null;
  return (
    <Pagination
      count={pageCount}
      // MUI Pagination is 1-based; our page index is 0-based.
      page={Math.min(page, pageCount - 1) + 1}
      onChange={(_, oneBasedPage) => onPageChange(null, oneBasedPage - 1)}
      siblingCount={1}
      boundaryCount={1}
      showFirstButton
      showLastButton
      shape="rounded"
      color="primary"
      size="small"
      sx={{ ml: 2 }}
    />
  );
};

type Props = {
  columns: Column[];
  data: Record<string, any>[];
  titleHeader?: string;
  isSearchable?: boolean;
  labelButtonHeader?: string;
  labelButtonFooter?: string;
  iconNameButton?: string;
  showIconEye?: boolean;
  tableMaxHeight?: string | number;
  tableMinHeight?: string | number;
  rowsPerPageOptions?: number[];
  showPagination?: boolean;
  showSelectedField?: boolean;
  iconButton?: string;
  isBackgroundUnique?: boolean;
  componentHeader?: React.ReactNode;
  tablePagnationCount?: number;
  isFilterFromBackend?: boolean;
  /** Controlled current page (0-based) when driving pagination from the backend. */
  pageProps?: number;
  /** Controlled rows-per-page when driving pagination from the backend. */
  rowsPerPageProps?: number;
  handleChangePageProps?: (newPage: number) => void;
  /** Notified when the user changes the rows-per-page selector. */
  handleChangeRowsPerPageProps?: (newRowsPerPage: number) => void;
  onSelectRow?: (selectedRows: Record<string, any>[]) => void;
  onChangeSearchTerm?: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onClickButtonFooter?: () => void;
  onClickButtonEye?: () => void;
  onClickIconButton?: () => void;
  resetPaginationTrigger?: boolean;
};

type Column = {
  header: string;
  field: string;
  render?: (rowData: Record<string, any>) => React.ReactNode;
};

export const CustomTable: FC<Props> = ({
  columns,
  data,
  tablePagnationCount,
  titleHeader,
  isSearchable = false,
  labelButtonHeader,
  labelButtonFooter,
  iconNameButton,
  showIconEye,
  tableMaxHeight,
  tableMinHeight,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
  showPagination = true,
  showSelectedField,
  iconButton,
  isBackgroundUnique = false,
  isFilterFromBackend = false,
  componentHeader,
  pageProps,
  rowsPerPageProps,
  onSelectRow,
  handleChangePageProps,
  handleChangeRowsPerPageProps,
  onChangeSearchTerm,
  onClickButtonFooter,
  onClickButtonEye,
  onClickIconButton,
  resetPaginationTrigger = false,
}) => {
  const classes = useStyles({
    isSearchable,
    showPagination,
    isBackgroundUnique,
    isTitleHeader: titleHeader ? true : false,
  });

  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string;
  } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    rowsPerPageOptions[0] ?? DEFAULT_ROWS_PER_PAGE
  );
  const [selectedRows, setSelectedRows] = useState<Record<string, any>[]>([]);

  // When the parent controls the page (backend pagination), use those values.
  const isPageControlled = pageProps !== undefined;
  const currentPage = isPageControlled ? (pageProps as number) : page;
  // Effective rows-per-page: prefer the controlled value when provided.
  const effectiveRowsPerPage =
    rowsPerPageProps !== undefined ? rowsPerPageProps : rowsPerPage;

  const resetPagination = () => {
    setPage(0);
    setRowsPerPage(rowsPerPageOptions[0] ?? DEFAULT_ROWS_PER_PAGE);
  };

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPaginationTrigger]);

  const handleChangePage = (_: unknown, newPage: number) => {
    // Always notify the parent (backend pagination) and keep local state in
    // sync for client-side mode. Never block the change with clamping here.
    handleChangePageProps && handleChangePageProps(newPage);
    if (!isPageControlled) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (rowsPerPageProps === undefined) {
      setRowsPerPage(newRowsPerPage);
    }
    handleChangeRowsPerPageProps && handleChangeRowsPerPageProps(newRowsPerPage);
    // Reset to the first page whenever the page size changes.
    handleChangePageProps && handleChangePageProps(0);
    if (!isPageControlled) {
      setPage(0);
    }
  };

  const requestSort = (key: string) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectRow = (row: Record<string, any>) => {
    const selectedIndex = selectedRows.findIndex(
      (selectedRow) => selectedRow === row
    );

    let newSelected: Record<string, any>[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }

    setSelectedRows(newSelected);

    if (onSelectRow) {
      onSelectRow(newSelected);
    }
  };

  const handleSelectAllRows = () => {
    const allRowsSelected = selectedRows.length === data.length;

    const newSelectedRows = allRowsSelected ? [] : [...data];

    setSelectedRows(newSelectedRows);

    if (onSelectRow) {
      onSelectRow(newSelectedRows);
    }
  };

  const isSearch = !isFilterFromBackend && searchTerm?.trim()?.length > 0;

  // Full client-side search across every row (only used in client-side mode).
  const filteredDataSearchTerm = useMemo(
    () =>
      (data || []).filter((item) =>
        Object?.values(item)?.some((value) =>
          value?.toString()?.toLowerCase()?.includes(searchTerm.toLowerCase())
        )
      ),
    [data, searchTerm]
  );

  // The dataset the client-side pagination should operate on:
  // the search result when searching, otherwise the raw data.
  const clientBaseData = isSearch ? filteredDataSearchTerm : data || [];

  // Sort the base client-side dataset (backend mode leaves data untouched).
  const sortedClientData = useMemo(() => {
    if (isFilterFromBackend) return data || [];
    if (!sortConfig) return clientBaseData;

    return [...clientBaseData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientBaseData, sortConfig, isFilterFromBackend, data]);

  // Total number of rows the paginator should report.
  const paginationCount = isFilterFromBackend
    ? tablePagnationCount ?? 0
    : sortedClientData.length;

  // Clamp the page whenever the client-side dataset shrinks below the current
  // offset (e.g. after deleting the last row of the last page).
  // In backend/controlled mode the parent owns the page, so we never clamp.
  const totalPages = Math.max(
    1,
    Math.ceil((paginationCount || 0) / effectiveRowsPerPage)
  );
  useEffect(() => {
    if (isPageControlled) return;
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  // Rows actually rendered.
  const filteredData = useMemo(() => {
    if (isFilterFromBackend) return data || [];
    if (!showPagination) return sortedClientData;

    return sortedClientData.slice(
      currentPage * effectiveRowsPerPage,
      (currentPage + 1) * effectiveRowsPerPage
    );
  }, [
    isFilterFromBackend,
    data,
    showPagination,
    sortedClientData,
    currentPage,
    effectiveRowsPerPage,
  ]);

  // The page index handed to MUI. In backend/controlled mode we trust the
  // parent value verbatim so page navigation is never snapped back. In
  // client mode we guard against an out-of-range index.
  const displayedPage = isPageControlled
    ? currentPage
    : Math.min(
        currentPage,
        Math.max(0, Math.ceil(paginationCount / effectiveRowsPerPage) - 1)
      );

  return (
    <motion.div
      className={classes.container}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className={classes.headerContainer}>
        {titleHeader && titleHeader?.trim()?.length > 0 && (
          <CustomTypography
            variant={"h5"}
            text={titleHeader}
            isUppercase
            color={colors.darkblack}
          />
        )}

        {labelButtonHeader && labelButtonHeader?.trim()?.length > 0 && (
          <CustomButtonTable
            variant={"secondary"}
            textVariant={"link"}
            label={labelButtonHeader}
            width={"fit-content"}
            fontWeight={"bold"}
            textDecoration={"none"}
            iconName={iconNameButton}
            padding={"0px 10px 0px 10px"}
          />
        )}

        {isSearchable && (
          <FormControl
            className={`${classes.formControl} ${classes.searchControl}`}
            variant="outlined"
          >
            <OutlinedInput
              placeholder={t("common.find", "Find")}
              /* The input had no accessible name — a screen reader announced
                 only "edit text". The placeholder is not a label. */
              inputProps={{ "aria-label": t("common.find", "Find") }}
              type="search"
              onChange={
                isFilterFromBackend
                  ? onChangeSearchTerm
                  : (e) => {
                      setSearchTerm(e.target.value);
                      // Jump back to the first page when a new search starts.
                      setPage(0);
                    }
              }
              endAdornment={
                <InputAdornment position="end">
                  <Icon
                    name="search"
                    color={colors.darkblack}
                    width={"15px"}
                    height={"15px"}
                  />
                </InputAdornment>
              }
            />
          </FormControl>
        )}

        {componentHeader && (
          <div className={classes.componentHeader}>{componentHeader}</div>
        )}

        {iconButton && iconButton?.trim()?.length > 0 && (
          <CustomIconButton
            iconName={iconButton}
            widthIcon={"20px"}
            heightIcon={"20px"}
            onClick={onClickIconButton}
          />
        )}
      </div>

      <TableContainer
        className={classes.containerTable}
        style={{
          maxHeight: tableMaxHeight,
          overflowY: "auto",
          minHeight: tableMinHeight,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {showSelectedField && (
                <TableCell>
                  <CustomCheckBox
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < data.length
                    }
                    checked={
                      data.length > 0 && selectedRows.length === data.length
                    }
                    onChange={handleSelectAllRows}
                  />
                </TableCell>
              )}

              {columns.map((column, index) => (
                <TableCell key={index}>
                  <div className={classes.iconSort}>
                    <CustomTypography
                      text={column.header}
                      variant={"link"}
                      onClick={() => requestSort(column.field)}
                    />
                    {sortConfig && sortConfig.key === column.field && (
                      <>
                        {sortConfig.direction === "asc" ? (
                          <Icon name="chevronUp" />
                        ) : (
                          <Icon name="chevronDown" />
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
              ))}
              {showIconEye && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData?.map((item, rowIndex) => (
              <TableRow key={rowIndex}>
                {showSelectedField && (
                  <TableCell>
                    <CustomCheckBox
                      checked={selectedRows.indexOf(item) !== -1}
                      onChange={() => handleSelectRow(item)}
                    />
                  </TableCell>
                )}
                {columns.map((column, colIndex) => (
                  <TableCell key={colIndex}>
                    {column?.render ? (
                      column?.render(item)
                    ) : (
                      <CustomTypography
                        text={item[column.field]}
                        variant="default"
                      />
                    )}
                  </TableCell>
                ))}

                {showIconEye && (
                  <TableCell>
                    <IconButton onClick={onClickButtonEye}>
                      <Icon
                        name="eye"
                        width={"24px"}
                        height={"24px"}
                        color={colors.BLACK}
                      />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}

            {(!filteredData || filteredData.length === 0) && (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (showSelectedField ? 1 : 0) +
                    (showIconEye ? 1 : 0)
                  }
                  style={{ textAlign: "center" }}
                >
                  <CustomTypography
                    text={t("common.noData", "No data")}
                    variant="default"
                    color={colors.greydark}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.footerContainer}>
        {showPagination && (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={paginationCount}
            rowsPerPage={effectiveRowsPerPage}
            page={displayedPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t("common.of", "sur")} ${
                count !== -1 ? count : `${to}+`
              }`
            }
            labelRowsPerPage={t("common.rowsPerPage", "")}
            ActionsComponent={(subProps) => (
              <PaginationActions
                count={subProps.count}
                page={subProps.page}
                rowsPerPage={subProps.rowsPerPage}
                onPageChange={subProps.onPageChange}
              />
            )}
          />
        )}
        {labelButtonFooter && labelButtonFooter?.trim()?.length > 0 && (
          <CustomButtonModal
            variant={"default"}
            textVariant={"link"}
            label={labelButtonFooter}
            width={"fit-content"}
            textDecoration={"none"}
            iconName={"chevronRight"}
            padding={"0px 10px 0px 10px"}
            onClick={onClickButtonFooter}
          />
        )}
      </div>
    </motion.div>
  );
};
